<template>
  <div>
    <!-- Inline trigger: thumbnails + attach link -->
    <div class="flex flex-wrap items-center justify-start">
      <div
        class="relative w-20 h-20 mr-3 shadow-lg mb-2 border border-neutral-200 rounded-lg"
        v-for="file in uploadedFiles"
        :key="file.id + '_img'"
      >
        <img :src="file.preview" class="object-cover h-20 w-full rounded-lg" alt />
        <button
          type="button"
          class="absolute -top-2 -right-2 bg-gray-100 rounded-full p-1 shadow hover:bg-gray-200"
          :aria-label="$t('Remove')"
          :title="$t('Remove')"
          @click.prevent="removeUploaded(file.id)"
        >
          <svg class="w-4 h-4 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
    <input
      type="hidden"
      :name="name"
      v-for="file in uploadedFiles"
      :key="file.id"
      :value="file.signed_id"
    />
    <a
      href="#"
      :id="upyId"
      class="inline-flex items-center text-sm text-neutral-800 hover:no-underline hover:text-neutral-600"
      @click.prevent="openModal"
    >
      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
      </svg>
      <span v-if="attachFile">
        {{ (attachFile[0] || uploadedFiles[0]) ? $t("Replace") : $t("Attach") }}&nbsp;{{ noun }}
      </span>
      <span v-else>
        {{ uploadedFiles.length > 0 ? $t("Replace") : $t("Attach") }}&nbsp;{{ noun }}
      </span>
    </a>

    <!-- Moved to <body> on mount: the board sidebar is position:sticky, which
         traps fixed children in its stacking context under the post list. -->
    <div ref="portal">
    <!-- Modal overlay -->
    <transition name="showUp">
      <div
        v-if="showModal"
        class="fixed inset-0 bg-black bg-opacity-25 z-[9998]"
        @click.self="closeModal"
      ></div>
    </transition>

    <!-- Modal content -->
    <transition name="comeIn">
      <div
        v-if="showModal"
        class="fixed inset-0 flex z-[9999]"
        @click.self="closeModal"
      >
        <div
          class="upload-modal bg-white m-auto w-full flex flex-col"
          :style="modalCardStyle"
        >
          <!-- Header -->
          <div class="flex items-center px-5 pt-5 pb-3 flex-shrink-0">
            <h4 class="font-semibold text-neutral-900">{{ $t("Upload images") }}</h4>
            <a href="#" class="text-neutral-700 hover:text-neutral-600 ml-auto" @click.prevent="closeModal">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
              </svg>
            </a>
          </div>

          <!-- Body (scrollable) -->
          <div class="px-5 pb-5 overflow-y-auto flex-1">
            <!-- Empty state drop zone -->
            <div
              v-if="pendingFiles.length === 0"
              class="drop-zone border-2 border-dashed p-10 text-center transition-colors"
              :class="isDragging ? 'dragging' : ''"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleDrop"
            >
              <p class="text-sm text-neutral-800" style="font-size: 15px">
                {{ $t("Drop files here or") }}
                <a href="#" class="accent-link font-semibold hover:opacity-80" @click.prevent="triggerFileInput">{{ $t("browse") }}</a>
              </p>
              <p class="text-xs text-neutral-500 mt-1">{{ $t("Images only, up to 10MB each, 10 files max") }}</p>
            </div>

            <!-- File list -->
            <div v-else>
              <div v-for="pf in pendingFiles" :key="pf.id" class="flex items-center bg-gray-50 rounded-xl p-3 mb-2">
                <div class="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                  <img v-if="pf.preview" :src="pf.preview" class="w-full h-full object-cover" alt />
                </div>
                <div class="ml-3 flex-1 min-w-0">
                  <p class="text-sm font-medium text-neutral-900 truncate">{{ pf.name }}</p>
                  <p class="text-xs text-neutral-500">{{ formatSize(pf.size) }}</p>
                  <div v-if="pf.error" class="text-xs text-red-500 mt-0.5">{{ pf.error }}</div>
                  <div v-else-if="isUploading && !pf.uploaded" class="mt-1.5">
                    <div class="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        class="progress-fill h-1.5 rounded-full transition-all duration-200"
                        :style="{ width: pf.progress + '%' }"
                      ></div>
                    </div>
                  </div>
                  <div v-else-if="pf.uploaded" class="text-xs text-green-600 mt-0.5">{{ $t("Uploaded") }}</div>
                </div>
                <button
                  v-if="!isUploading"
                  class="ml-2 text-neutral-500 hover:text-red-500 flex-shrink-0"
                  @click="removeFile(pf.id)"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
                </button>
              </div>

              <!-- Add more (compact, gray) -->
              <div
                v-if="!isUploading && !uploadComplete && pendingFiles.length < maxFiles"
                class="add-more-zone border-2 border-dashed border-gray-200 rounded-xl p-3 text-center text-sm transition-colors mt-2"
                :class="isDragging ? 'dragging' : ''"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
              >
                <a href="#" class="text-neutral-600 font-medium hover:text-neutral-800" @click.prevent="triggerFileInput">+ {{ $t("Add more") }}</a>
              </div>
            </div>

            <!-- Validation errors -->
            <div v-if="validationErrors.length > 0" class="mt-3">
              <p v-for="(err, i) in validationErrors" :key="i" class="text-xs text-red-500">{{ err }}</p>
            </div>

            <!-- Action button -->
            <button
              v-if="validPendingFiles.length > 0"
              class="upload-btn w-full mt-4 py-3 rounded-xl font-semibold text-sm text-white transition-colors"
              :class="actionButtonClass"
              :disabled="isUploading && !uploadComplete"
              @click="handleAction"
            >
              <span v-if="uploadComplete">{{ $t("Done") }}</span>
              <span v-else-if="isUploading">{{ $t("Uploading...") }}</span>
              <span v-else>{{ $t("Upload") }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
    </div>

    <!-- Hidden file input -->
    <input ref="fileInput" type="file" accept="image/*" multiple class="hidden" @change="handleFileSelect" />
  </div>
</template>

<script>
import { DirectUpload } from "@rails/activestorage";
import EventBus from "event_bus";

let fileIdCounter = 0;
const MAX_FILES = 10;
// Keep in step with DirectUploadsController::CONTENT_TYPES; the server refuses the rest.
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp", "image/tiff",
  "image/avif", "image/heic", "image/heif", "image/x-icon", "image/vnd.microsoft.icon",
];

export default {
  // editor-input-ids: input ids of RichTextEditors whose pasted/dropped files
  // should land in this modal (see RichTextEditor#handleFileAccept).
  props: ["name", "noun", "attach-file", "upy-id", "editor-input-ids"],
  data() {
    return {
      uploadedFiles: [],
      showModal: false,
      pendingFiles: [],
      isDragging: false,
      isUploading: false,
      uploadComplete: false,
      validationErrors: [],
      maxFiles: MAX_FILES,
    };
  },
  computed: {
    modalCardStyle() {
      return {
        maxWidth: '520px',
        maxHeight: 'calc(100vh - 80px)',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
      };
    },
    validPendingFiles() {
      return this.pendingFiles.filter(f => !f.error);
    },
    actionButtonClass() {
      if (this.uploadComplete) return "is-done";
      if (this.isUploading) return "is-uploading";
      return "is-ready";
    },
  },
  mounted() {
    document.body.appendChild(this.$refs.portal);

    this.imagesUploadHandler = () => {
      this.uploadedFiles = [];
    };
    EventBus.$on("imagesUpload", this.imagesUploadHandler);

    this.fileRedirectHandler = ({ inputId, file }) => {
      if (!this.editorInputIds || !this.editorInputIds.includes(inputId)) return;
      this.acceptRedirectedFile(file);
    };
    EventBus.$on("richTextFileRedirect", this.fileRedirectHandler);

    this.directUploadUrl = document
      .querySelector("meta[name='direct-upload-url']")
      .getAttribute("content");

    this._onKeydown = (e) => {
      if (e.key === "Escape" && this.showModal) this.closeModal();
    };
    document.addEventListener("keydown", this._onKeydown);
  },
  beforeDestroy() {
    EventBus.$off("imagesUpload", this.imagesUploadHandler);
    EventBus.$off("richTextFileRedirect", this.fileRedirectHandler);
    document.removeEventListener("keydown", this._onKeydown);
    document.body.style.overflow = "";
    this.$refs.portal.remove();
  },
  methods: {
    openModal() {
      this.pendingFiles = [];
      this.validationErrors = [];
      this.isUploading = false;
      this.uploadComplete = false;
      this.showModal = true;
      document.body.style.overflow = "hidden";
    },
    closeModal() {
      if (this.isUploading && !this.uploadComplete) return;
      this.showModal = false;
      document.body.style.overflow = "";
    },
    acceptRedirectedFile(file) {
      if (this.isUploading && !this.uploadComplete) return;
      if (this.uploadComplete) this.finishAndClose();
      if (!this.showModal) this.openModal();
      this.addFiles([file]);
    },
    triggerFileInput() {
      this.$refs.fileInput.value = "";
      this.$refs.fileInput.click();
    },
    handleFileSelect(e) {
      this.addFiles(e.target.files);
    },
    handleDrop(e) {
      this.isDragging = false;
      this.addFiles(e.dataTransfer.files);
    },
    addFiles(fileList) {
      this.validationErrors = [];
      const remaining = MAX_FILES - this.pendingFiles.length;
      if (remaining <= 0) {
        this.validationErrors.push(this.$t("Maximum 10 files allowed"));
        return;
      }
      const filesToAdd = Array.from(fileList).slice(0, remaining);
      if (fileList.length > remaining) {
        this.validationErrors.push(this.$t("Maximum 10 files allowed") + " — " + (fileList.length - remaining) + " skipped");
      }
      for (const file of filesToAdd) {
        const error = this.validateFile(file);
        const id = ++fileIdCounter;
        const entry = {
          id,
          file,
          name: file.name,
          size: file.size,
          preview: null,
          progress: 0,
          error,
          signed_id: null,
          uploaded: false,
        };
        this.pendingFiles.push(entry);
        if (!error) this.generatePreview(entry);
      }
    },
    validateFile(file) {
      if (file.type === "image/svg+xml") return this.$t("SVG files are not allowed");
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return this.$t("Not an image file");
      if (file.size > 10485760) return this.$t("File too large (max 10MB)");
      return null;
    },
    generatePreview(entry) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.$set(entry, "preview", e.target.result);
      };
      reader.readAsDataURL(entry.file);
    },
    removeFile(id) {
      this.pendingFiles = this.pendingFiles.filter(f => f.id !== id);
    },
    handleAction() {
      if (this.uploadComplete) {
        this.finishAndClose();
      } else {
        this.uploadFiles();
      }
    },
    uploadFiles() {
      const toUpload = this.validPendingFiles;
      if (toUpload.length === 0) return;

      this.isUploading = true;
      this.uploadComplete = false;

      let completed = 0;
      const total = toUpload.length;

      toUpload.forEach(entry => {
        const delegate = {
          directUploadWillStoreFileWithXHR: (request) => {
            request.upload.addEventListener("progress", (event) => {
              if (event.total > 0) {
                this.$set(entry, "progress", Math.round((event.loaded / event.total) * 100));
              }
            });
          },
        };

        const upload = new DirectUpload(entry.file, this.directUploadUrl, delegate);
        upload.create((error, blob) => {
          if (error) {
            this.$set(entry, "error", this.$t("Upload failed"));
          } else {
            this.$set(entry, "signed_id", blob.signed_id);
            this.$set(entry, "uploaded", true);
            this.$set(entry, "progress", 100);
          }
          completed++;
          if (completed === total) {
            this.uploadComplete = true;
          }
        });
      });
    },
    finishAndClose() {
      this.uploadedFiles = this.validPendingFiles
        .filter(f => f.uploaded && f.signed_id)
        .map(f => ({
          id: f.id,
          preview: f.preview,
          signed_id: f.signed_id,
        }));
      EventBus.$emit("uploadedImage", true);
      this.closeModal();
    },
    removeUploaded(id) {
      this.uploadedFiles = this.uploadedFiles.filter(f => f.id !== id);
      EventBus.$emit("uploadedImage", this.uploadedFiles.length > 0);
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / 1048576).toFixed(1) + " MB";
    },
  },
};
</script>

<style scoped>
/* Transitions — matches dialog.vue pattern */
.showUp-enter-active,
.showUp-leave-active {
  transition: opacity 200ms cubic-bezier(0.165, 0.84, 0.44, 1);
}
.showUp-enter,
.showUp-leave-to {
  opacity: 0;
}
.showUp-leave,
.showUp-enter-to {
  opacity: 1;
}
.comeIn-enter-active,
.comeIn-leave-active {
  transition: opacity 200ms cubic-bezier(0.165, 0.84, 0.44, 1),
              transform 200ms cubic-bezier(0.165, 0.84, 0.44, 1);
}
.comeIn-enter,
.comeIn-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}
.comeIn-leave,
.comeIn-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* Modal card — mobile responsive */
@media screen and (max-width: 640px) {
  .upload-modal {
    width: 90% !important;
  }
}

/* Drop zones */
.drop-zone {
  border-radius: 20px;
  border-color: #e5e7eb;
}
.drop-zone:hover,
.drop-zone.dragging {
  border-color: var(--accent-color, #F54239);
  background-color: rgba(254, 242, 242, 0.3);
}
.add-more-zone:hover,
.add-more-zone.dragging {
  border-color: #9ca3af;
  background-color: #f9fafb;
}

/* Accent-colored links */
.accent-link {
  color: var(--accent-color, #F54239);
}

/* Progress bar fill */
.progress-fill {
  background-color: var(--accent-color, #F54239);
}

/* Upload button states */
.upload-btn.is-ready {
  background-color: var(--accent-color, #F54239);
}
.upload-btn.is-ready:hover {
  opacity: 0.9;
}
.upload-btn.is-uploading {
  background-color: #9ca3af;
  cursor: wait;
}
.upload-btn.is-done {
  background-color: #22c55e;
}
.upload-btn.is-done:hover {
  background-color: #16a34a;
}
</style>
